import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";
import type { PaymentMethod } from "./pos";

export type LayawayStatus = "ABIERTO" | "COMPLETADO" | "CANCELADO";

export const LAYAWAY_STATUS_LABEL: Record<LayawayStatus, string> = {
  ABIERTO: "Abierto",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};

/** Métodos de abono que excluyen CREDITO — un apartado es dinero real entregado, no un saldo diferido. */
export type LayawayPaymentMethod = Exclude<PaymentMethod, "CREDITO">;

export interface LayawayLineRecord {
  id: string;
  productId: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  discountAmount: string;
  subtotal: string;
  product?: { id: string; sku: string; name: string; unit: string };
}

export interface LayawayPaymentRecord {
  id: string;
  layawayId: string;
  amount: string;
  paymentMethod: LayawayPaymentMethod;
  paymentDate: string;
  notes: string | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
}

export interface LayawayRecord {
  id: string;
  branchId: string;
  branch?: { id: string; name: string };
  storeCustomerId: string | null;
  storeCustomer?: { id: string; name: string; documentId: string | null; phone: string | null } | null;
  customerName: string | null;
  customerPhone: string | null;
  layawayNumber: number;
  startDate: string;
  subtotal: string;
  taxTotal: string;
  discountTotal: string;
  principal: string;
  balance: string;
  status: LayawayStatus;
  notes: string | null;
  saleId: string | null;
  sale?: { id: string; saleNumber: number; saleDate: string; total: string } | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
  lines: LayawayLineRecord[];
  payments: LayawayPaymentRecord[];
}

export interface ListLayawaysFilters {
  page?: number;
  pageSize?: number;
  storeCustomerId?: string;
  status?: LayawayStatus;
  branchId?: string;
}

/** Apartados de tienda: el cliente separa mercancía con un abono inicial y la termina de pagar en varias visitas — ver layaway.service.ts en el backend. No reserva stock hasta completarse. */
export function useLayaways(filters: ListLayawaysFilters = {}) {
  return useQuery({
    queryKey: ["store", "layaways", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<LayawayRecord[]>>("/layaways", { params: filters });
      return data;
    },
  });
}

export function useLayaway(id: string | null) {
  return useQuery({
    queryKey: ["store", "layaways", "detail", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<LayawayRecord>>(`/layaways/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface CreateLayawayPayload {
  branchId: string;
  storeCustomerId?: string;
  customerName?: string;
  customerPhone?: string;
  notes?: string;
  lines: Array<{ productId: string; quantity: number | string }>;
  depositAmount: number | string;
  depositMethod?: LayawayPaymentMethod;
}

export function useCreateLayaway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLayawayPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<LayawayRecord>>("/layaways", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "layaways"] }),
  });
}

export interface RegisterLayawayPaymentPayload {
  id: string;
  amount: number | string;
  paymentMethod?: LayawayPaymentMethod;
  paymentDate?: string;
  notes?: string;
}

export function useRegisterLayawayPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: RegisterLayawayPaymentPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<LayawayRecord>>(`/layaways/${id}/payments`, body);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "layaways"] }),
  });
}

export function useCancelLayaway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<LayawayRecord>>(`/layaways/${id}/cancel`, {});
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "layaways"] }),
  });
}

export interface CompleteLayawayPayload {
  id: string;
  paymentMethod?: PaymentMethod;
  amountPaid: number | string;
  creditInstallments?: number;
  creditFirstDueDate?: string;
  creditIntervalDays?: number;
  /** Ver pos.service.ts::createSale — los precios se recalculan VIGENTES al completar, pueden quedar por debajo del costo aunque no lo estuvieran al apartar. */
  acknowledgeBelowCost?: boolean;
}

/** Completa el apartado (el cliente se lleva la mercancía) creando la venta real del POS — requiere turno de caja abierto. Ver layaway.service.ts::completeLayaway. */
export function useCompleteLayaway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: CompleteLayawayPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<LayawayRecord>>(`/layaways/${id}/complete`, body);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "layaways"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
      queryClient.invalidateQueries({ queryKey: ["store", "credits"] });
    },
  });
}
